'use client';


import ImageProxy from '@next-image-proxy';

import useDisableRedirectOnLogin from '@shared/hooks/useDisableRedirectOnLogin';

import CustomStoryBanner from '../CustomStoryBanner';
import CustomStoryLayout from '../CustomStoryLayout';
import CustomStoryStatCardsList from '../CustomStoryStatCardsList';
import CampaignLeaderboard from '../ui/CampaignLeaderboard/CampaignLeaderboard';
import { partners, stats, team } from './data';
import StepToJoin5tarsCampaign from './StepToJoin5tarsCampaign';

const FiveTarsCampaignStory = () => {
  useDisableRedirectOnLogin();

  return (
    <CustomStoryLayout
      title="5TARS Project Overview and Campaign Details"
      banner={
        <CustomStoryBanner
          alt="5TARS Road-to-IDO $250,000 in 5TARS Token Rewards. Magic Square. 5tars"
          desktop={{
            src: '/store/stories/5tars/5tars-story-banner-desktop.png',
            width: 3484,
            height: 592,
            quality: 80,
          }}
          tablet={{
            src: '/store/stories/5tars/5tars-story-banner-tablet.png',
            width: 2580,
            height: 1340,
            quality: 80,
          }}
          mobile={{
            src: '/store/stories/5tars/5tars-story-banner-mobile.png',
            width: 556,
            height: 190,
            quality: 80,
          }}
        />
      }
    >
      <div className="story-prose">
        <h2>Project Introduction and Highlights</h2>

        <p>
          5TARS.io is the world’s largest Web3 sports prediction and fantasy gaming platform,
          integrating NFTs and rewarding user engagement with cryptocurrency and real-world assets.
        </p>

        <h2>
          5TARS.io integrates blockchain, NFTs, and a dynamic earning model to create an immersive
          and rewarding experience for sports fans.
        </h2>

        <p>
          Launched in August 2023, 5TARS.io has rapidly grown, recording over 1 million on-chain
          transactions per month. The platform aims to blend the excitement of sports with the
          innovation of Web3, providing fans with unique digital assets, real-world rewards, and an
          engaging community. With a user-friendly interface and a free-to-play model, 5TARS.io
          democratizes access to the gaming experience, offering an engaging and rewarding
          environment for sports fans worldwide.
        </p>

        <ImageProxy
          src="/store/stories/5tars/5tars-story-about.png"
          width={3486}
          height={1816}
          sizes="100vw"
          quality={100}
          alt="5TARS The World’s largest Web3 Sports Fantasy Gaming Platform"
        />

        <h2>Key Highlights:</h2>

        <ul>
          <li>5TARS.io ranks among the top games in web3.</li>
          <li>
            The platform has achieved more than 1 million on-chain transactions per month since
            launch.
          </li>
          <li>
            Users can collect and trade unique digital assets, adding tangible value and enhancing
            the gaming experience.
          </li>
          <li>
            Players earn STARCOIN through accurate sports predictions and strategic gameplay,
            combining skill and rewards.
          </li>
          <li>
            STARCOIN can be redeemed for gift cards and other real-world assets, bridging digital
            and physical economies.
          </li>
          <li>
            Starting with football, the platform will soon include other major sports like cricket,
            increasing its appeal to a broader audience.
          </li>
        </ul>

        <h2>Unique Value Proposition</h2>

        <p>
          5TARS.io revolutionizes the sports gaming industry by seamlessly integrating blockchain,
          NFTs, and an innovative earning model to create an unparalleled fan experience. The
          platform transforms passive spectators into active participants, rewarding their sports
          knowledge and strategic gameplay with valuable digital assets and real-world rewards. By
          lowering entry barriers with a free-to-play model and expanding into major sports like
          cricket, 5TARS.io democratizes access to the excitement of fantasy sports. The platform
          not only enhances fan engagement but also bridges the gap between the digital and physical
          worlds, making every moment of the game more thrilling and rewarding.
        </p>

        <h2>Investors & Partners</h2>

        <ul className="story-prose-none grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {partners.map(({ name, image }) => {
            return (
              <li
                key={name}
                className="overflow-hidden rounded-lg flex items-center justify-center bg-neutral-50 h-20"
              >
                <ImageProxy
                  width={557}
                  height={161}
                  alt={name}
                  src={image.url}
                  className="w-auto max-w-full h-auto max-h-full"
                />
              </li>
            );
          })}
        </ul>

        <h2>Team</h2>

        <ul className="story-prose-none grid md:grid-cols-2 gap-8 lg:gap-10">
          {team.map((teamMember) => {
            return (
              <li key={teamMember.name} className="flex max-sm:flex-col gap-4 lg:gap-8">
                <ImageProxy
                  alt={`${teamMember.name}'s avatar`}
                  src={teamMember.avatar}
                  width={210}
                  height={210}
                  className="flex-shrink-0 size-[6.5rem] rounded-full"
                />

                <div className="flex flex-col [&>*]:m-0">
                  <p className="text-base leading-[1.75] font-semibold">{teamMember.name}</p>
                  <p className="text-base leading-[1.75] font-medium text-neutral-500">
                    {teamMember.position}
                  </p>
                  <ul className="text-sm leading-[1.71] font-medium list-disc pl-[1.5em]">
                    <li>{teamMember.description}</li>
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>

        <h2>Token Utility / Use Cases</h2>

        <p className="mb-4">
          The $5TARS token plays a crucial role within the 5TARS.io ecosystem, offering various
          utilities that drive its demand and value:
        </p>

        <ol>
          <li>
            <b>In-Game Transactions:</b> $5TARS tokens are used to purchase unique digital
            collectibles (NFTs), special in-game items, player cards, and access to premium events
            and arenas. This enhances the gaming experience by allowing players to unlock exclusive
            content and capabilities, driving continuous demand for the tokens.
          </li>
          <li>
            <b>Staking and Rewards:</b> Players can stake $5TARS tokens to earn an attractive annual
            percentage yield (APY) and receive additional perks such as discounts and access to
            special features. Staking incentivizes long-term holding, reducing market supply and
            increasing token value.
          </li>
          <li>
            <b>Governance:</b> Token holders can participate in governance decisions, voting on key
            aspects of the platform&apos;s development and future updates. This empowers the
            community, ensuring the platform evolves in a way that aligns with user interests,
            thereby increasing the token&apos;s utility and demand.
          </li>
          <li>
            <b>Rewards and Incentives:</b> $5TARS tokens are distributed as rewards for achieving
            in-game milestones, winning tournaments, and participating in community events. These
            rewards drive engagement and encourage active participation, ensuring a steady demand
            for the tokens.
          </li>
          <li>
            <b>Real-World Asset Conversion:</b> Players can redeem in-game currency (STARCOIN)
            earned through gameplay for $5TARS tokens, which can then be converted to real-world
            assets like gift cards. This bridges the gap between the digital and physical worlds,
            adding tangible value to in-game achievements and attracting a broader audience.
          </li>
        </ol>

        <h2>Token Deflation</h2>

        <p className="mb-4">
          To ensure the value of the $5TARS token grows over time, several strategies have been
          implemented to reduce the total supply and increase demand:{' '}
        </p>

        <ol>
          <li>
            <b>Burning Tokens on Purchases:</b> 25% of all tokens used to purchase NFTs and any
            in-game assets are burned, significantly reducing the total token supply and driving up
            the value of the remaining tokens.
          </li>
          <li>
            <b>Staking and Lock-up Mechanisms:</b> By staking $5TARS tokens, users can earn rewards.
            These tokens are locked up for a certain period, reducing the number of tokens in
            circulation and helping stabilize the token price.
          </li>
          <li>
            <b>Transaction Fees:</b> Small fees are applied to trades within the 5TARS marketplace
            and other platform services. A portion of these fees is used to buy back and burn
            tokens, further decreasing the supply.
          </li>
          <li>
            <b>In-Game Utility:</b> $5TARS tokens are essential for various in-game activities, such
            as purchasing NFTs, entering tournaments, and accessing premium features. This creates
            constant demand for the tokens, supporting their value.
          </li>
          <li>
            <b>Limited Token Supply:</b> There is a maximum limit to the total number of $5TARS
            tokens that can ever exist. This cap prevents inflation and helps maintain the token’s
            value.
          </li>
          <li>
            <b>Strategic Partnerships and Integrations:</b> Partnerships with other platforms where
            $5TARS tokens can be used increase their utility and demand. These partnerships help
            drive adoption and long-term value growth.
          </li>
        </ol>

        <h2>Campaign Details</h2>

        <p className="mb-3">
          To celebrate the upcoming release of the $5TARS Token by 5TARS.io, $250,000 in 5TARS Token
          Rewards has been allocated to the 5TARS Road-to-IDO Campaign. This campaign aims to expand
          the 5TARS ecosystem and community, rewarding the most active users on the platform and
          across social media. Your level of activity and engagement directly increases your
          potential to earn more $5TARS tokens!
        </p>
        <p className="mt-0">
          Magic Square is proud to host and manage this campaign, offering complete support to all
          participants.
        </p>

        <CustomStoryStatCardsList items={stats} />

        <p className="border border-primary-200 rounded-lg bg-neutral-50 text-center p-6 text-secondary-600 text-lg md:text-[2rem] md:leading-[1.1875] font-semibold">
          Rewards will be distributed based on XP, and we will also hold a weekly raffle to give
          everyone a chance to win
        </p>

        <h2>Steps to Join the Campaign</h2>

        <StepToJoin5tarsCampaign />

        <h2>Leaderboard</h2>

        <CampaignLeaderboard
          id="leaderboard"
          className="story-prose-none"
          campaignSlug="5tars-ido"
        />
      </div>
    </CustomStoryLayout>
  );
};

export default FiveTarsCampaignStory;
